import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Printer, ArrowRight, Stamp, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fileUrlToFull } from "@/components/ImageUpload";
import { formatDateTime } from "@/lib/format";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

const FIELD_LABEL_TYPE = {
  text: "نص", textarea: "نص طويل", number: "رقم", email: "بريد",
  phone: "هاتف", date: "تاريخ", select: "قائمة", checkbox: "خانات",
  radio: "اختيار", file: "ملف", signature: "توقيع",
};

function isImageFile(v) {
  return v && typeof v === "object" && v.type && v.type.startsWith("image/");
}

function renderValue(field, v, fontSize = 12) {
  if (v == null || v === "") return <span className="text-muted-foreground">—</span>;
  if (Array.isArray(v)) return <span>{v.join("، ")}</span>;
  if (typeof v === "object" && v.url) {
    if (isImageFile(v)) {
      return <img src={fileUrlToFull(v.url)} alt={field.label} className="max-h-32 object-contain border border-border" />;
    }
    return <a href={fileUrlToFull(v.url)} className="text-primary underline">{v.name}</a>;
  }
  return <span style={{ fontSize }}>{String(v)}</span>;
}

// A4 = 210 x 297mm. Print CSS uses mm directly.
function CustomLayoutPage({ data, layout, settings }) {
  const fields = data.template?.fields || [];
  return (
    <div
      className="relative bg-white"
      style={{ width: "210mm", minHeight: "297mm" }}
    >
      <PageHeader data={data} layout={layout} settings={settings} />

      {(layout.positions || []).map((p) => {
        const f = fields.find((x) => x.id === p.field_id);
        if (!f) return null;
        const v = data.values?.[p.field_id];
        return (
          <div
            key={p.field_id}
            className="absolute overflow-hidden"
            style={{
              left: `${p.x}mm`,
              top: `${p.y}mm`,
              width: `${p.width}mm`,
              minHeight: `${p.height}mm`,
              fontSize: `${p.font_size || 10}pt`,
            }}
          >
            {p.show_label !== false && (
              <div className="text-[8pt] text-muted-foreground font-medium mb-0.5 leading-tight">
                {f.label}
              </div>
            )}
            <div className="border-b border-foreground/30 pb-0.5 leading-tight">
              {renderValue(f, v, p.font_size || 10)}
            </div>
          </div>
        );
      })}

      {layout.show_footer !== false && <PageFooter settings={settings} />}
    </div>
  );
}

function DefaultLayoutPage({ data, layout, settings }) {
  return (
    <div className="bg-white" style={{ width: "210mm", minHeight: "297mm" }}>
      <div className="p-10">
        <PageHeader data={data} layout={layout} settings={settings} inline />

        <div className="border border-border mt-6">
          {(data.template?.fields || []).map((f, idx) => {
            const v = data.values?.[f.id];
            return (
              <div key={f.id} className={`grid grid-cols-3 ${idx > 0 ? "border-t border-border" : ""}`}>
                <div className="col-span-1 bg-surface px-4 py-3 border-e border-border text-sm font-medium text-muted-foreground">
                  {f.label}
                </div>
                <div className="col-span-2 px-4 py-3 text-sm break-words">
                  {renderValue(f, v)}
                </div>
              </div>
            );
          })}
        </div>

        {layout.show_footer !== false && (
          <div className="mt-10 pt-5 border-t border-border flex items-end justify-between text-xs text-muted-foreground">
            <div>
              <div>{settings?.pdf_footer_text || "تم إنشاء هذه الاستمارة إلكترونياً عبر نظام إدارة الجوازات"}</div>
              <div className="font-mono mt-1">{formatDateTime(new Date())}</div>
            </div>
            <div className="text-end">
              <div className="border-b border-foreground pb-1 mb-1 w-40 h-12"></div>
              <div>توقيع الموظف المختص</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PageHeader({ data, layout, settings, inline = false }) {
  const tmpl = data.template || {};
  const logo = tmpl.logo_url || settings?.logo_url;
  const title = tmpl.header_title || tmpl.title || settings?.pdf_default_header_title || "وثيقة رسمية";
  const subtitle = tmpl.header_subtitle || settings?.pdf_default_header_subtitle || "PASSPORT DEPARTMENT";
  const containerClass = inline
    ? "flex items-start justify-between border-b-2 border-primary pb-5 mb-2"
    : "absolute inset-x-0 top-0 px-10 pt-8 pb-3 border-b-2 border-primary flex items-start justify-between";
  return (
    <div className={containerClass} style={inline ? {} : { height: "60mm" }}>
      <div className="flex items-center gap-3">
        {logo ? (
          <img src={fileUrlToFull(logo)} alt="logo" className="w-14 h-14 object-contain" />
        ) : (
          <div className="w-14 h-14 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
            <Stamp className="w-7 h-7" />
          </div>
        )}
        <div>
          <div className="font-display text-2xl font-bold">{title}</div>
          <div className="text-xs text-muted-foreground tracking-wider uppercase">{subtitle}</div>
          <div className="mt-2 text-xs text-muted-foreground">
            <span>المُقدِّم: </span><span className="font-medium text-foreground">{data.submitted_by_name}</span>
            <span className="mx-2">•</span>
            <span className="font-mono">{formatDateTime(data.submitted_at)}</span>
          </div>
        </div>
      </div>
      <div className="text-end flex flex-col items-end gap-2">
        {layout.show_form_number !== false && (
          <div>
            <div className="text-[10px] uppercase text-muted-foreground tracking-wider">رقم الاستمارة</div>
            <div className="font-mono font-bold text-xl text-primary">{data.form_number}</div>
          </div>
        )}
        {layout.show_qr !== false && (
          <div className="bg-white p-1 border border-border">
            <QRCodeSVG value={data.qr_code_data} size={80} />
          </div>
        )}
      </div>
    </div>
  );
}

function PageFooter({ settings }) {
  return (
    <div className="absolute inset-x-0 bottom-0 px-10 py-3 border-t border-border flex items-end justify-between text-[9pt] text-muted-foreground">
      <div>
        <div>{settings?.pdf_footer_text || "تم إنشاء هذه الاستمارة إلكترونياً عبر نظام إدارة الجوازات"}</div>
        <div className="font-mono mt-1">{formatDateTime(new Date())}</div>
      </div>
      <div className="text-end">
        <div className="border-b border-foreground pb-1 mb-1 w-40 h-10"></div>
        <div>توقيع الموظف المختص</div>
      </div>
    </div>
  );
}

export default function PrintViewPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/submissions/${id}`).then((r) => {
      setData(r.data);
      setLoading(false);
      if (params.get("print") === "1") {
        setTimeout(() => window.print(), 600);
      }
    }).catch((e) => { toast.error(formatApiError(e)); setLoading(false); });
  }, [id, params]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">الاستمارة غير موجودة</div>;

  const layout = data.template?.pdf_layout || { mode: "default", show_qr: true, show_form_number: true, show_footer: true };

  return (
    <div dir="rtl" className="min-h-screen bg-surface">
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          html, body { background: white !important; }
          .no-print { display: none !important; }
          .print-page { box-shadow: none !important; border: none !important; margin: 0 !important; page-break-after: always; }
        }
      `}</style>
      <div className="no-print bg-card border-b border-border px-6 py-3 flex items-center justify-between sticky top-0 z-20">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="print-back">
          <ArrowRight className="w-4 h-4 ms-1" /> رجوع
        </Button>
        <div className="text-sm text-muted-foreground">
          التخطيط: {layout.mode === "custom" ? "مخصص" : "افتراضي"}
        </div>
        <Button onClick={() => window.print()} data-testid="print-action" className="bg-primary hover:bg-primary/90">
          <Printer className="w-4 h-4 ms-1" /> طباعة / حفظ PDF
        </Button>
      </div>

      <div className="my-8 mx-auto print-page" style={{ width: "210mm", boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)" }}>
        {layout.mode === "custom" && layout.positions?.length > 0 ? (
          <CustomLayoutPage data={data} layout={layout} settings={settings} />
        ) : (
          <DefaultLayoutPage data={data} layout={layout} settings={settings} />
        )}
      </div>
    </div>
  );
}
